import { formatRelative } from "date-fns";
import { enAU } from "date-fns/locale";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ActivityIndicator from "../components/ActivityIndicator";
import { Layout } from "../components/Layout";
import StatusIcon from "../components/StatusIcon";
import { Notification } from "../models/notification";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";

type PendingListScreenProps = { userInfo: UserInfo };

const PendingListScreen: React.FC<PendingListScreenProps> = (props) => {
  const [pendingNotifications, setPendingNotifications] =
    useState<Notification[]>();
  const [approvedNotifications, setApprovedNotifications] =
    useState<Notification[]>();
  const [viewing, setViewing] = useState("pending");
  const [loading, setLoading] = useState(false);
  const init = async () => {
    setLoading(true);
    const newPendingNotifications =
      await notificationService.getPendingNotifications();
    setPendingNotifications(newPendingNotifications);
    const newApprovedNotifications =
      await notificationService.getApprovedNotifications();
    setApprovedNotifications(newApprovedNotifications);
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const titleFormatted = (title: string, template: string = "") => {
    if (template) {
      return template;
    }
    return title?.substring(0, 40) || "No title";
  };

  const renderPendingNotifications = () => {
    return (
      <>
        <div className="grid grid-cols-4 border-b">
          <p className="">Notification title</p>
          <p className="">Submitted by</p>
          <p className="">Send date</p>
          <p className="">View</p>
        </div>
        {pendingNotifications ? (
          <>
            {pendingNotifications.length > 0 ? (
              pendingNotifications.map((n) => (
                <div className="grid grid-cols-4 my-6" key={n.uuid}>
                  <p>{titleFormatted(n.Title, n.EmailTemplate)}</p>
                  <p>{n.AuthorName || "Unnamed"}</p>
                  <p className="capitalize">
                    {formatRelative(new Date(n.SendAt), new Date(), {
                      locale: enAU,
                    })}
                  </p>
                  <p>
                    <Link
                      to={`/approvals/${n.uuid}`}
                      className={
                        "bg-slate-700 text-white text-xs rounded px-5 py-2"
                      }
                    >
                      View
                    </Link>
                  </p>
                </div>
              ))
            ) : (
              <p>No records.</p>
            )}
          </>
        ) : (
          <ActivityIndicator />
        )}
      </>
    );
  };

  const renderApprovedNotifications = () => {
    return (
      <>
        <div className="grid grid-cols-5 border-b">
          <p className="">Notification title</p>
          <p className="">Submitted by</p>
          <p className="">Status</p>
          <p className="">Send date</p>
          <p className="">View</p>
        </div>
        {approvedNotifications ? (
          <>
            {approvedNotifications.length > 0 ? (
              approvedNotifications.map((n) => (
                <div className="grid grid-cols-5 my-6" key={n.uuid}>
                  <p>{titleFormatted(n.Title, n.EmailTemplate)}</p>
                  <p>{n.AuthorName || "Unnamed"}</p>
                  <StatusIcon status={n.Status} />
                  <p className="capitalize">
                    {formatRelative(new Date(n.SendAt), new Date(), {
                      locale: enAU,
                    })}
                  </p>
                  <p>
                    <Link
                      to={`/approvals/${n.uuid}`}
                      className={
                        "bg-slate-700 text-white text-xs rounded px-5 py-2"
                      }
                    >
                      View
                    </Link>
                  </p>
                </div>
              ))
            ) : (
              <p>No records.</p>
            )}
          </>
        ) : (
          <ActivityIndicator />
        )}
      </>
    );
  };

  return (
    <Layout userInfo={props.userInfo} loading={loading}>
      <div className="pt-8 px-20">
        <div className="mb-4">
          <span className="text-sm">Viewing:</span>
          <select
            className="text-sm"
            value={viewing}
            onChange={(e) => setViewing(e.currentTarget.value)}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved / Sent</option>
          </select>
        </div>
        {viewing === "pending"
          ? renderPendingNotifications()
          : renderApprovedNotifications()}
      </div>
    </Layout>
  );
};

export default PendingListScreen;
